import { CheckCircle2, ArrowRight } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Helmet } from "react-helmet-async"

const americanCornerCtx = require.context("../../../assets/AmericanCorner", false, /\.(jpg|jpeg|png|webp)$/i)
const getImg = (s) => (typeof s === "string" ? s : s?.default ?? s)
const IPL_HERO = americanCornerCtx.keys().length > 0 ? getImg(americanCornerCtx(americanCornerCtx.keys().find((k) => !k.includes("Fulgence")) || americanCornerCtx.keys()[0])) : null

export default function Ipl() {
    return (
        <div className="min-h-screen bg-background font-sans text-foreground">
            <Helmet>
                <title>Institut de Pédagogie des Langues (IPL) - Université de Ngozi</title>
                <meta name="description" content="Découvrez l'Institut de Pédagogie des Langues (IPL) de l'Université de Ngozi. Une formation d'excellence en Anglais, Français et Swahili pour les futurs enseignants et communicateurs." />
                <meta name="keywords" content="IPL, Pédagogie des langues, Anglais, Français, Swahili, Université de Ngozi" />
            </Helmet>

            {/* Hero Section - AmericanCorner/languages domain */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {IPL_HERO && <img src={IPL_HERO} alt="" className="w-full h-full object-cover opacity-40 mix-blend-luminosity scale-105" aria-hidden />}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Institut de Pédagogie</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Vol. 2024</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">L'Art de</span>
                            <span className="block text-white mt-2">Transmettre</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Formez-vous aux métiers de l'éducation et de la médiation linguistique.
                                <span className="italic block mt-1 tracking-tight">Innover pour éduquer.</span>
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                Candidater Maintenant
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                            <Button variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white px-10 py-7 text-sm font-black rounded-xl backdrop-blur-sm">
                                Découvrir les Cursus
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Stats - Newspaper Style Dateline */}
            <div className="border-y border-border bg-secondary/10">
                <div className="container py-4 flex flex-wrap items-center gap-8">
                    {[
                        { val: "STAGES", label: "Dès la L1" },
                        { val: "LABS", label: "Langues Modernes" },
                        { val: "FLE/FLS", label: "Spécialisation" },
                        { val: "100%", label: "Insertion" }
                    ].map((stat, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="text-xl font-black text-primary">{stat.val}</span>
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</span>
                            {i < 3 && <span className="hidden lg:block text-border ml-4 font-light">/</span>}
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Article - Magazine Layout */}
            <article className="container section-padding-sm">
                <div className="grid lg:grid-cols-[250px_1fr] gap-12 lg:gap-20">
                    {/* Sidebar - Meta Info */}
                    <aside className="lg:sticky lg:top-8 lg:self-start hidden lg:block">
                        <nav className="space-y-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground mb-6 pb-2 border-b border-border">
                                Sommaire
                            </p>
                            {["Mission", "Cursus", "Avantages"].map((item, i) => (
                                <a key={i} href={`#${item.toLowerCase()}`} className="group flex items-start gap-3 py-2 text-xs font-bold text-muted-foreground hover:text-primary transition-smooth">
                                    <span className="text-accent font-black">0{i + 1}</span>
                                    {item}
                                </a>
                            ))}
                        </nav>

                        <div className="mt-12 pt-8 border-t border-border">
                            <div className="bg-primary/5 p-6 border-l-4 border-primary">
                                <h4 className="text-[10px] uppercase tracking-[0.2em] font-black mb-4 text-primary">En Bref</h4>
                                <dl className="space-y-4 text-[11px]">
                                    <div className="flex justify-between items-baseline border-b border-border/40 pb-2">
                                        <dt className="font-bold">Durée</dt>
                                        <dd className="text-muted-foreground">3 ans</dd>
                                    </div>
                                    <div className="flex justify-between items-baseline">
                                        <dt className="font-bold">Diplôme</dt>
                                        <dd className="text-muted-foreground">Licence</dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </aside>

                    {/* Main Body */}
                    <main className="space-y-16">
                        {/* Section 01 - Mission */}
                        <section id="mission">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Notre Mission
                            </h2>

                            <div className="prose-sm max-w-none space-y-4 text-muted-foreground font-medium leading-relaxed">
                                <p>
                                    L'IPL forme des enseignants qualifiés et des professionnels de la communication linguistique par une approche pédagogique active.
                                </p>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4 mt-8">
                                {[
                                    "Pédagogie différenciée",
                                    "Didactique des langues",
                                    "Psychologie de l'éducation",
                                    "TICE & Innovation"
                                ].map((skill, i) => (
                                    <div key={i} className="flex items-center gap-3 p-3 bg-secondary/5 border border-border/50 rounded-sm">
                                        <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                                        <span className="text-[11px] font-bold uppercase tracking-tight">{skill}</span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Section 02 - Cursus */}
                        <section id="cursus">
                            <h2 className="heading-4 text-foreground mb-8 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Cursus de Formation
                            </h2>

                            <div className="grid md:grid-cols-2 gap-6">
                                {[
                                    { title: "Français & Langues", tags: ["Littérature", "Linguistique", "FLE"] },
                                    { title: "Anglais & Traduction", tags: ["Interpretation", "Business", "Literature"] },
                                    { title: "Sciences de l'Education", tags: ["Gestion", "Psychologie", "Innovation"] }
                                ].map((filiere, i) => (
                                    <div key={i} className="interactive-card p-6 border-l-4 border-primary transition-smooth">
                                        <h3 className="heading-4 mb-4">{filiere.title}</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {filiere.tags.map((tag, j) => (
                                                <span key={j} className="px-2 py-1 bg-secondary/50 text-[10px] font-bold uppercase tracking-wider rounded-sm text-primary">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                    </main>
                </div>
            </article>

            {/* Final CTA - Visual Punch */}
            <footer className="bg-primary text-white border-t-8 border-accent">
                <div className="container py-16 text-center max-w-2xl">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black mb-4 block">Prochaine Étape</span>
                    <h2 className="heading-2 mb-8 leading-tight">
                        Formez la prochaine<br />génération.
                    </h2>
                    <Button className="bg-accent text-accent-foreground px-10 py-4 text-base font-black hover:bg-accent/90 transition-smooth group rounded-md shadow-2xl border-b-4 border-black/10">
                        Postuler Maintenant
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                </div>
            </footer>
        </div>
    )
}
